import type { QuestionGroupProps } from "../../../../../../components/Questionnaire/Section/Question/types";
import { isQuestion } from "../../../../../../components/Questionnaire/Section/Question/types";
import type { VisibilityMap } from "../../../../../../components/Questionnaire/validation/form/types";
import type { Answers } from "../../../../types";
import type { FlattenGroupResult } from "../types";
import { getPrimaryAnswer, createEmptyFlattenResult } from "../utils";

/**
 * personalised_cover_question_group
 * Functions in a similar way to the yesNoToggleGroup, but group questions are displayed visually as 'children' of the primary question
 * (without technically being nested).
 */
export const flattenPersonalisedCoverGroup = (
  group: Partial<QuestionGroupProps>,
  answers: Answers,
  visibility: VisibilityMap,
): FlattenGroupResult => {
  const { primaryQuestion, groupQuestions } = group;
  const filteredQuestions = groupQuestions?.filter(isQuestion) ?? [];

  // If the primary question is missing, or all the questions are hidden, return an empty flatten group
  if (!primaryQuestion) {
    return createEmptyFlattenResult(answers, visibility);
  }

  const primaryAnswer = getPrimaryAnswer(primaryQuestion, answers);

  // Map questions with answers and set visibility based on primary answer
  const visibilityByQuestionName = { ...visibility };

  const questionsWithVisibility = filteredQuestions.map(q => {
    // Set visibility: true when primary is "yes", false when "no"
    visibilityByQuestionName[q.name] =
      primaryAnswer === "yes" ? !!q.visible : false;

    return { ...q, includeAnswer: !!q.visible };
  });

  // Merge primary question and flattened group questions
  return {
    questions: [primaryQuestion, ...questionsWithVisibility],
    groups: {
      [primaryQuestion.name]: {
        kind: "personalised_cover",
        names: questionsWithVisibility.map(q => q.name),
      },
    },
    visibility: visibilityByQuestionName,
    answers: {
      ...answers,
      [primaryQuestion.name]: primaryAnswer || "",
    },
  };
};
